import React, { useState, useEffect } from "react";
import { Form, Formik } from "formik";
import { useParams, useNavigate } from "react-router-dom";
import FormField from "../components/forms/FormField";
import StyleModal from "../components/StyleSelector/StyleModal";
import PreviewSection from "../components/StoryPreview/PreviewSection";
import SpeechToText from "../components/SpeechToText/SpeechToText";
import LoadingOverlay from "../components/LoadingOverlay/LoadingOverlay";
import TutorialSlideshow from "../components/TutorialSlideshow/TutorialSlideshow";
import { DESIGN_LOADING_STATES, STORY_LOADING_STATES } from "../components/LoadingOverlay/LoadingStates";
import { bookStyles, visualStyles, writingStyles } from "../data/styleOptions";
import { createPageValidationSchema } from "../validation/createPageSchema";
import StyleOptions from "./CreatePage/components/StyleOptions";
import PageOptions from "./CreatePage/components/PageOptions";
import { useEditPage } from "./EditPage/hooks/useEditPage";
import { useBook } from '../contexts/BookContext';
import StoryDebugTools from "../components/helpers/StoryDebugTools";
import ErrorModal from "../components/ErrorModal/ErrorModal";

export default function EditPage() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { currentBook, loading, error, loadBook } = useBook();
  const [activeModal, setActiveModal] = useState(null);
  const [initialStyles, setInitialStyles] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  // State for error modal
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalTitle, setErrorModalTitle] = useState('');
  const [errorModalMessage, setErrorModalMessage] = useState('');
  
  // State for tutorial modal
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  
  
  // Listen for story generation error events
  useEffect(() => {
    const handleStoryGenerationError = (event) => {
      console.log('Story generation error received:', event.detail);
      setErrorModalTitle(event.detail.title || 'Story Generation Error');
      setErrorModalMessage(event.detail.message || 'There was a problem generating your story. Please try again.');
      setErrorModalOpen(true);
    };
    
    window.addEventListener('storyGenerationError', handleStoryGenerationError);
    
    return () => {
      window.removeEventListener('storyGenerationError', handleStoryGenerationError);
    };
  }, []);
  // Using operationType from useEditPage instead of isGeneratingPreview

  const {
    showPreview,
    currentPage,
    pageContents,
    selectedStyles,
    isProcessing,
    setIsProcessing,
    operationType,
    setOperationType,
    handleStyleSelect,
    handleGeneratePreview,
    handlePageChange,
    handleContentChange,
    wordsPerPage,
    setSelectedStyles,
    setPageContents,
    setShowPreview,
    handleSaveBasicInfo,
    handleSavePreview,
    handleContinueWithoutSave,
    loadingUIMessage,
    canGoToDesign,
    goToDesignRoute,
    canCancelProcessing,
    handleCancelProcessing,
    draftedResult,
    isSavingBasicInfo,
    overlayDefaultMinimized
  } = useEditPage(bookId);

  // Effect to load the book when component mounts
  useEffect(() => {
    if (bookId) {
      loadBook(bookId);
    }
  }, [bookId, loadBook]);
  
  // NEW: Add an effect to expose the current book data to the preview component
  // This allows the PreviewSection to get the current version content directly
  useEffect(() => {
    if (!currentBook) return;
    
    // Create a simple handler to expose current book data when requested
    const handleRequestBookData = (event) => {
      console.log('EditPage: Received request for current book data');
      if (event.detail?.bookId === bookId) {
        // Extract the spreads data from the current book
        console.log('EditPage: Providing current book data');
        
        // Create and dispatch a response event with the current book data
        const responseEvent = new CustomEvent('currentBookDataResponse', {
          detail: { bookData: currentBook }
        });
        window.dispatchEvent(responseEvent);
      }
    };
    
    // Also expose a global function as a fallback
    window.currentBookData = currentBook;
    
    // Listen for requests for the current book data
    window.addEventListener('requestCurrentBookData', handleRequestBookData);
    
    return () => {
      window.removeEventListener('requestCurrentBookData', handleRequestBookData);
      delete window.currentBookData;
    };
  }, [currentBook, bookId]);

  useEffect(() => {
    if (currentBook && !initialStyles) {
      const styles = {
        bookStyle: currentBook.bookStyle || null,
        visualStyle: currentBook.visualStyle || null,
        writingStyle: currentBook.writingStyle || null,
      };
      // Set initial styles only on first load
      setSelectedStyles(styles);
      setInitialStyles(styles);
      
      // We no longer need to parse fullStoryContent or pages here
      // since the useEditPage hook now directly handles loading from book elements
    }
  }, [currentBook, initialStyles, setSelectedStyles, setPageContents, setShowPreview]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => loadBook(bookId)}
            className="text-indigo-600 hover:text-indigo-500"
          >
            Refetch Book
          </button>
          <button
            onClick={() => navigate('/library')}
            className="text-indigo-600 hover:text-indigo-500"
          >
            Return to Library
          </button>
        </div>
      </div>
    );
  }

  if (!currentBook) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Book not found</div>
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => loadBook(bookId)}
            className="text-indigo-600 hover:text-indigo-500"
          >
            Refetch Book
          </button>
          <button
            onClick={() => navigate('/library')}
            className="text-indigo-600 hover:text-indigo-500"
          >
            Return to Library
          </button>
        </div>
      </div>
    );
  }

  const initialValues = {
    title: currentBook.title || '',
    storyContent: currentBook.storyContent || '',
    totalPages: currentBook.totalPages || '32',
    pageSize: currentBook.pageSize || 'us-trade',
  };

  // Debug tools are only shown if explicitly enabled via localStorage
  const showDebugTools = localStorage.getItem('showDebugTools') === 'true';
  const canVisitWorld = !!(currentBook && currentBook.world);

  return (
    <>
      {showDebugTools && <StoryDebugTools />}
      <ErrorModal 
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title={errorModalTitle}
        message={errorModalMessage}
      />
      <div className="flex items-center justify-between mb-4 px-1">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Story</h1>
          <p className="text-gray-600">Write your story and choose styles.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowTutorialModal(true)}
            className="px-4 py-2 rounded-md bg-yellow-100 text-yellow-900 hover:bg-yellow-200 font-medium"
          >
            Tutorial
          </button>
          {canVisitWorld && (
            <button
              onClick={() => navigate(`/world/${bookId}`)}
              className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
            >
              Continue without saving
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-full overflow-hidden">
        <Formik
          initialValues={initialValues}
          validationSchema={createPageValidationSchema}
          enableReinitialize={true}
          onSubmit={() => {}}
          validateOnChange={true}
          validateOnBlur={true}
          validateOnMount={true}
        >
          {({ values, dirty, setFieldValue, errors, validateForm, touched, setTouched }) => {
            const hasStyleChanges = initialStyles && (
              selectedStyles.bookStyle?.id !== initialStyles.bookStyle?.id ||
              selectedStyles.visualStyle?.id !== initialStyles.visualStyle?.id ||
              selectedStyles.writingStyle?.id !== initialStyles.writingStyle?.id
            );
            const hasChanges = dirty || hasStyleChanges;

            return (
            <>
              <div className="bg-white p-6 rounded-lg shadow-md overflow-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Edit Your Story
                  </h2>
                  {hasChanges && (
                    <button
                      type="button"
                      onClick={() => {
                        setInitialStyles(selectedStyles);
                        handleSaveBasicInfo(values);
                      }}
                      disabled={isSavingBasicInfo}
                      className={`py-2 px-4 rounded-md transition-colors text-sm font-medium ${
                        isSavingBasicInfo
                          ? 'bg-blue-400 text-white cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isSavingBasicInfo ? 'Saving...' : 'Save Changes'}
                    </button>
                  )}
                </div>

                <Form className="space-y-6">
                  <FormField
                    label="Story Title"
                    name="title"
                    type="text"
                    placeholder="Enter your story title"
                    options={[]}
                  />

                  <StyleOptions
                    selectedStyles={selectedStyles}
                    onStyleSelect={handleStyleSelect}
                    onModalOpen={setActiveModal}
                    validationErrors={validationErrors}
                  />

                  <PageOptions
                    wordsPerPage={wordsPerPage}
                    onPageChange={handlePageChange}
                    onContentChange={handleContentChange}
                    isWordsPerPageDisabled={true}
                    /* Page size is now handled by the Formik form directly */
                  />

                  <FormField
                    label="Story Content"
                    name="storyContent"
                    type="textarea"
                    rows={6}
                    placeholder="Type or paste your story here..."
                    options={[]}
                    labelAction={
                      <SpeechToText
                        onTranscriptionComplete={(text) => setFieldValue('storyContent', text)}
                      />
                    }
                  />

                  <button
                    type="button"
                    onClick={() => {
                      // Check values directly
                      const errors = {};
                      if (!values.title?.trim()) errors.title = 'Title is required';
                      if (!values.storyContent?.trim()) errors.storyContent = 'Story content is required';
                      if (!selectedStyles.bookStyle) errors.bookStyle = 'Book style is required';
                      if (!selectedStyles.visualStyle) errors.visualStyle = 'Visual style is required';
                      if (!selectedStyles.writingStyle) errors.writingStyle = 'Writing style is required';
                      
                      if (Object.keys(errors).length > 0) {
                        setValidationErrors(errors);
                        setTouched({
                          title: true,
                          storyContent: true,
                        });
                        return;
                      }
                      
                      setValidationErrors({});
                      handleGeneratePreview(values);
                    }}
                    disabled={isProcessing}
                    className={`w-full py-2 px-4 rounded-md transition-colors font-medium flex items-center justify-center gap-2 ${
                      isProcessing
                        ? 'bg-indigo-400 text-white cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {isProcessing && (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    )}
                    {isProcessing
                      ? operationType === 'design' 
                        ? 'Design generating...'
                        : 'Generating Story Preview...'
                      : 'Generate Preview'
                    }
                  </button>
                </Form>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md overflow-auto">
                {/* Calculate if we have actual page content to show */}
                {(() => {
                  // Check for content in three places:
                  // 1. Current pageContents (from state)
                  // 2. Saved preview content in localStorage 
                  // 3. Completed storyProgress in localStorage
                  
                  // Original check for current content
                  const hasContent = pageContents && 
                    pageContents.filter((content, index) => content && (index + 1) % 2 === 1).length > 0;
                  
                  // NEW: Check for persisted content or completed storyProgress
                  let hasPersistedContent = false;
                  let hasCompletedProgress = false;
                  
                  try {
                    // Check for saved preview content
                    const savedPreviewContent = localStorage.getItem(`currentGeneratedPreview-${currentBook?.id}`);
                    if (savedPreviewContent) {
                      const parsedContent = JSON.parse(savedPreviewContent);
                      hasPersistedContent = Array.isArray(parsedContent) && parsedContent.length > 0;
                      console.log(`EditPage: Found persisted preview content: ${hasPersistedContent}`);
                    }
                    
                    // Also check for completed storyProgress
                    const storyProgress = localStorage.getItem(`storyProgress-${currentBook?.id}`);
                    if (storyProgress) {
                      const progress = JSON.parse(storyProgress);
                      hasCompletedProgress = progress.status === 'completed';
                      console.log(`EditPage: Found completed storyProgress: ${hasCompletedProgress}`);
                      
                      // If we have completed progress, also set hasDesign flag to ensure content loads properly
                      if (hasCompletedProgress) {
                        localStorage.setItem(`hasDesign-${currentBook?.id}`, 'true');
                        console.log(`EditPage: Set hasDesign flag for book: ${currentBook?.id}`);
                      }
                    }
                  } catch (e) {
                    console.error('Error checking localStorage for content:', e);
                  }
                  
                  // Show PreviewSection if we have any content OR persisted content OR completed progress
                  const shouldShowPreview = showPreview || hasPersistedContent || hasCompletedProgress;
                  
                  return shouldShowPreview ? (
                  <PreviewSection
                    key={bookId}
                    currentPage={currentPage}
                    totalPages={parseInt(values.totalPages, 10)}
                    pageContents={pageContents}
                    onPageChange={handlePageChange}
                    onContentChange={handleContentChange}
                    onSaveBasicInfo={() => handleSaveBasicInfo(values)}
                    onSavePreview={(previewContents, isDesignMode) => handleSavePreview(previewContents, values, isDesignMode)}
                    onContinueWithoutSave={handleContinueWithoutSave}
                    bookId={currentBook?.id}
                    isProcessing={isProcessing}
                    operationType={operationType}
                    draftedResult={draftedResult}
                  />
                  ) : (
                    <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <p className="text-gray-500">
                        Your story preview will appear here after generation
                      </p>
                    </div>
                  );
                })()}
              </div>

              {/* Style Selection Modals */}
              <StyleModal
                isOpen={activeModal === "book"}
                onClose={() => setActiveModal(null)}
                title="Select Book Style"
                description="Choose a style that best fits your story"
                options={bookStyles}
                selectedOption={selectedStyles.bookStyle}
                onSelect={(style) => {
                  handleStyleSelect("bookStyle", style);
                  setActiveModal(null);
                }}
              />

              <StyleModal
                isOpen={activeModal === "visual"}
                onClose={() => setActiveModal(null)}
                title="Select Visual Style"
                description="Choose how your story will look"
                options={visualStyles}
                selectedOption={selectedStyles.visualStyle}
                onSelect={(style) => {
                  handleStyleSelect("visualStyle", style);
                  setActiveModal(null);
                }}
              />

              <StyleModal
                isOpen={activeModal === "writing"}
                onClose={() => setActiveModal(null)}
                title="Select Writing Style"
                description="Choose your story's tone and style"
                options={writingStyles}
                selectedOption={selectedStyles.writingStyle}
                onSelect={(style) => {
                  handleStyleSelect("writingStyle", style);
                  setActiveModal(null);
                }}
              />
            </>
            );
          }}
        </Formik>
      </div>

      {/* Loading Overlay */}
      {isProcessing && (
        <LoadingOverlay
          states={loadingUIMessage}
          onComplete={() => {
            console.log('LoadingOverlay animation complete - keeping processing state active');
          }}
          loadingFor={operationType}
          canGoToDesign={canGoToDesign}
          goToDesignRoute={goToDesignRoute}
          goToWorldRoute={() => navigate(`/world/${bookId}`)}
          canCancel={canCancelProcessing}
          onCancel={handleCancelProcessing}
          draftedResult={draftedResult}
          defaultMinimized={overlayDefaultMinimized}
        />
      )}

      {/* Tutorial Modal */}
      {showTutorialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900">Tutorial</h2>
              <button
                onClick={() => setShowTutorialModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                aria-label="Close tutorial"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4">
              <TutorialSlideshow />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
